import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from './LanguageContext';
import { getTranslation } from './translations';
import LanguageSelector from './LanguageSelector';

function ResetPassword() {
  const { language, setLanguage } = useLanguage();
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const token = searchParams.get('token');
  const urlLanguage = searchParams.get('lang');
  
  // Set language from URL parameter if provided (only once on mount)
  useEffect(() => {
    if (urlLanguage && ['pl', 'en', 'fr'].includes(urlLanguage)) {
      setLanguage(urlLanguage);
    }
  }, []); // Empty dependency array means this only runs once on mount
  
  // Use current language context for all UI elements
  const currentLanguage = language;

  // Validation functions (same as in Register.js)
  const validatePassword = (pwd) => {
    if (!pwd || pwd.length < 8) {
      return getTranslation('passwordError', currentLanguage);
    }
    if (!/[0-9]/.test(pwd)) {
      return getTranslation('passwordDigitError', currentLanguage);
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd)) {
      return getTranslation('passwordSpecialError', currentLanguage);
    }
    return '';
  };

  const validateForm = () => {
    const newErrors = {};
    
    const passwordError = validatePassword(password);
    if (passwordError) newErrors.password = passwordError;
    
    if (password !== repeatPassword) {
      newErrors.repeatPassword = getTranslation('passwordMismatchError', currentLanguage);
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setErrors({});
    
    if (!token) {
      setMessage(getTranslation('invalid-reset-link', currentLanguage));
      return;
    }
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await fetch('/api/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          token,
          password,
          repeat_password: repeatPassword 
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        navigate('/password-reset-success');
      } else {
        let errorMsg = getTranslation('password-reset-error', currentLanguage);
        if (data.detail) {
          if (Array.isArray(data.detail)) {
            errorMsg = data.detail.map(err => err.msg).join(' ');
          } else if (typeof data.detail === 'string') {
            errorMsg = data.detail;
          }
        }
        setMessage(errorMsg);
      }
    } catch (error) {
      setMessage(getTranslation('serverError', currentLanguage));
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: '#f0f8ff',
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        padding: window.innerWidth <= 600 ? '20px 20px' : '40px 0',
        fontFamily: 'Nunito, Arial, sans-serif',
        position: 'relative'
      }}>
        {/* Language Selector */}
        <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000 }}>
          <LanguageSelector />
        </div>
        <div style={{
          background: 'white',
          borderRadius: window.innerWidth <= 600 ? 16 : 20,
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.2)',
          maxWidth: window.innerWidth <= 600 ? '95%' : 400,
          width: window.innerWidth <= 600 ? '95%' : 400,
          padding: window.innerWidth <= 600 ? 24 : 32,
          textAlign: 'center'
        }}>
          <h2 style={{ 
            color: '#ef4444', 
            marginBottom: 16, 
            fontFamily: 'Nunito, Arial, sans-serif', 
            fontWeight: 700, 
            fontSize: window.innerWidth <= 600 ? '1.5rem' : '1.8rem'
          }}>{getTranslation('error', currentLanguage)}</h2>
          <p style={{ color: '#6b7280', marginBottom: 24 }}>
            {getTranslation('invalid-reset-link', currentLanguage)}
          </p>
          <button 
            onClick={() => navigate('/forgot-password')}
            style={{
              padding: '12px 24px',
              borderRadius: 8,
              border: 'none',
              background: '#7c3aed',
              color: 'white',
              fontWeight: 700,
              cursor: 'pointer'
            }}
          >
            {getTranslation('request-new-link', currentLanguage)}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#f0f8ff',
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center', 
      justifyContent: 'center', 
      padding: window.innerWidth <= 600 ? '20px 20px' : '40px 0',
      fontFamily: 'Nunito, Arial, sans-serif',
      position: 'relative'
    }}>
      {/* Language Selector */}
      <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000 }}>
        <LanguageSelector />
      </div>
      {/* Main Title */}
      <h1 style={{
        fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
        fontWeight: 'bold',
        color: '#7c3aed',
        margin: '0 0 16px 0',
        textAlign: 'center',
        padding: window.innerWidth <= 600 ? '0 20px' : '0'
      }}>
        ShootMe
      </h1>
      
      {/* Subtitle */}
      <p style={{
        fontSize: window.innerWidth <= 600 ? '1rem' : '1.2rem',
        color: '#6b7280',
        margin: '0 0 48px 0',
        textAlign: 'center',
        maxWidth: window.innerWidth <= 600 ? '90%' : '600px',
        lineHeight: '1.6',
        padding: window.innerWidth <= 600 ? '0 20px' : '0'
      }}>
        {getTranslation('set-new-password', currentLanguage)}
      </p>

      <div style={{
        background: 'white',
        borderRadius: window.innerWidth <= 600 ? 16 : 20,
        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.2)',
        maxWidth: window.innerWidth <= 600 ? '95%' : 400,
        width: window.innerWidth <= 600 ? '95%' : 400,
        padding: window.innerWidth <= 600 ? 24 : 32,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
      }}>
        <h2 style={{ 
          color: '#7c3aed', 
          marginBottom: 24, 
          fontFamily: 'Nunito, Arial, sans-serif', 
          fontWeight: 700, 
          letterSpacing: 1,
          fontSize: window.innerWidth <= 600 ? '1.5rem' : '1.8rem',
          textAlign: 'center'
        }}>{getTranslation('new-password', currentLanguage)}</h2>
        
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          <div style={{ marginBottom: window.innerWidth <= 600 ? 16 : 18 }}>
            <label style={{ 
              color: '#4b4b4b', 
              fontWeight: 500,
              fontSize: window.innerWidth <= 600 ? '14px' : '16px'
            }}>{getTranslation('new-password', currentLanguage)}:</label><br />
            <input
              type="password"
              value={password}
              onChange={e => {
                setPassword(e.target.value);
                if (errors.password) {
                  const newErrors = { ...errors };
                  delete newErrors.password;
                  setErrors(newErrors);
                }
              }}
              onBlur={() => {
                const error = validatePassword(password);
                if (error) {
                  setErrors(prev => ({ ...prev, password: error }));
                }
              }}
              required
              disabled={isLoading}
              style={{ 
                width: '100%', 
                padding: window.innerWidth <= 600 ? 12 : 10, 
                borderRadius: 8, 
                border: errors.password ? '2px solid #ef4444' : '1px solid #bdbdbd', 
                marginTop: 4, 
                fontSize: window.innerWidth <= 600 ? '16px' : '16px',
                boxSizing: 'border-box',
                opacity: isLoading ? 0.6 : 1
              }}
            />
            {errors.password && (
              <div style={{ 
                color: '#ef4444', 
                fontSize: '12px', 
                marginTop: '4px' 
              }}>
                {errors.password}
              </div>
            )}
            <div style={{ 
              color: '#6b7280', 
              fontSize: '11px', 
              marginTop: '4px',
              lineHeight: '1.4'
            }}>
              {getTranslation('password-requirements', currentLanguage)}
            </div>
          </div>
          
          <div style={{ marginBottom: window.innerWidth <= 600 ? 16 : 18 }}>
            <label style={{ 
              color: '#4b4b4b', 
              fontWeight: 500,
              fontSize: window.innerWidth <= 600 ? '14px' : '16px'
            }}>{getTranslation('repeat-password', currentLanguage)}:</label><br />
            <input
              type="password"
              value={repeatPassword}
              onChange={e => {
                setRepeatPassword(e.target.value);
                if (errors.repeatPassword) {
                  const newErrors = { ...errors };
                  delete newErrors.repeatPassword;
                  setErrors(newErrors);
                }
              }}
              onBlur={() => {
                if (password !== repeatPassword) {
                  setErrors(prev => ({ ...prev, repeatPassword: 'Hasła nie są identyczne' }));
                }
              }}
              required
              disabled={isLoading}
              style={{ 
                width: '100%', 
                padding: window.innerWidth <= 600 ? 12 : 10, 
                borderRadius: 8, 
                border: errors.repeatPassword ? '2px solid #ef4444' : '1px solid #bdbdbd', 
                marginTop: 4, 
                fontSize: window.innerWidth <= 600 ? '16px' : '16px',
                boxSizing: 'border-box',
                opacity: isLoading ? 0.6 : 1
              }}
            />
            {errors.repeatPassword && (
              <div style={{ 
                color: '#ef4444', 
                fontSize: '12px', 
                marginTop: '4px' 
              }}>
                {errors.repeatPassword}
              </div>
            )}
          </div>
          
          <button 
            type="submit" 
            disabled={isLoading}
            style={{
              width: '100%',
              padding: window.innerWidth <= 600 ? '14px 0' : '12px 0',
              borderRadius: 8,
              border: 'none',
              background: isLoading ? '#9ca3af' : '#7c3aed',
              color: 'white',
              fontWeight: 700,
              fontSize: window.innerWidth <= 600 ? '16px' : '18px',
              letterSpacing: 1,
              boxShadow: isLoading ? 'none' : '0 4px 12px rgba(124, 58, 237, 0.3)',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              marginBottom: 8
            }}
          >
            {isLoading ? getTranslation('saving', currentLanguage) : getTranslation('change-password', currentLanguage)}
          </button>
        </form>
        
        {message && <div style={{ 
          marginTop: 18, 
          color: '#c62828', 
          fontWeight: 500,
          fontSize: window.innerWidth <= 600 ? '14px' : '16px',
          textAlign: 'center',
          padding: window.innerWidth <= 600 ? '0 10px' : '0'
        }}>{message}</div>}
      </div>
    </div>
  );
}

export default ResetPassword;
